// Pestaña Documentos: dos secciones de filas de archivo. Arriba el expediente de onboarding, que
// cuelga del directorio de Fiado; abajo los contratos, que cuelgan de cada crédito.
import { useMemo } from 'react';
import { File } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CustomerCreditDetail } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import type { CustomerDocumentsState } from '../api/useCustomerDocuments';
import { useCustomerCreditContracts } from '../api/useCustomerCreditContracts';
import {
    buildContractDocumentRows,
    buildKycDocumentRows,
    documentFileCountLabel,
    unreadContractsRow,
    type DocumentRow,
} from '../lib/customerDocuments';
import { CARD, CARD_HEAD } from './CustomerFileUi';

export function CustomerDocumentsTab({
    credit,
    documents,
    customerCreatedAt,
}: {
    credit: CustomerCreditDetail;
    /** El expediente de onboarding: otra lectura, otro permiso, y se cae por su cuenta. */
    documents: CustomerDocumentsState;
    /** El alta del cliente: la única fecha que el expediente puede usar si no trae la suya. */
    customerCreatedAt: string | null;
}) {
    // Alfabético por id: el orden de los créditos no puede cambiar entre renders.
    const creditIds = useMemo(
        () =>
            credit.state === 'ready'
                ? credit.data.credits
                      .map((item) => item.creditId)
                      .sort((a, b) => a.localeCompare(b))
                : [],
        [credit],
    );
    const contractReads = useCustomerCreditContracts(creditIds);

    const kycRows = buildKycDocumentRows({
        documents: documents.data,
        isLoading: documents.isLoading,
        failure: documents.failure,
        customerCreatedAt,
    });
    const contractRows = contractRowsOf(credit, creditIds, contractReads);

    return (
        <>
            <DocumentSection title="KYC · onboarding" rows={kycRows} />
            <DocumentSection title="Contractuales · por crédito" rows={contractRows} />
        </>
    );
}

/** Las filas contractuales, o el motivo por el que no se sabe qué créditos tiene el cliente. */
function contractRowsOf(
    credit: CustomerCreditDetail,
    creditIds: string[],
    readsByCredit: ReturnType<typeof useCustomerCreditContracts>,
): DocumentRow[] {
    if (credit.state === 'ready') return buildContractDocumentRows({ creditIds, readsByCredit });
    const unread = unreadContractsRow(credit.state);
    return unread ? [unread] : [];
}

/** Una tarjeta de documentos. La sección cuya lista queda vacía NO se pinta. */
function DocumentSection({ title, rows }: { title: string; rows: DocumentRow[] }) {
    if (rows.length === 0) return null;

    return (
        <section className={cn(CARD, 'overflow-hidden')}>
            <h3 className={cn(CARD_HEAD, 'flex items-center justify-between gap-2')}>
                <span>{title}</span>
                <span className="font-normal normal-case tabular-nums">
                    {documentFileCountLabel(rows)}
                </span>
            </h3>
            <div>
                {rows.map((row) => (
                    <DocumentRowItem key={row.key} row={row} />
                ))}
            </div>
        </section>
    );
}

/** Una fila: icono, nombre, sublínea y las acciones. Sin enlaces va apagada y dice por qué. */
function DocumentRowItem({ row }: { row: DocumentRow }) {
    const isDimmed = row.links.length === 0;

    return (
        <div className="grid grid-cols-[20px_1fr_auto_auto] items-center gap-2.5 border-b border-border/50 px-3 py-2 last:border-b-0">
            <File
                className={cn('size-4 flex-none text-muted-foreground', isDimmed && 'opacity-50')}
                aria-hidden
            />
            <div className="min-w-0">
                <div
                    className={cn(
                        'truncate text-[11.5px] font-semibold',
                        isDimmed && 'text-muted-foreground',
                    )}
                >
                    {row.name}
                </div>
                <div
                    title={row.note ?? undefined}
                    className={cn(
                        'mt-px truncate text-[10px] text-muted-foreground',
                        row.note ? 'cursor-help' : '',
                    )}
                >
                    {row.subLine}
                </div>
            </div>
            {row.links.map((link) => (
                <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded px-1.5 py-0.5 text-[10.5px] text-info hover:bg-info/10 hover:underline"
                >
                    {link.label}
                </a>
            ))}
        </div>
    );
}
